import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserGroupIcon,
  UserPlusIcon,
  BuildingOffice2Icon,
  Cog6ToothIcon,
  CheckCircleIcon,
  XCircleIcon,
  CheckBadgeIcon,
  DocumentTextIcon,
  KeyIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  IdentificationIcon,
  CalendarIcon,
  TrashIcon,
  NoSymbolIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  CardHeader,
  Badge,
  ContentLoader,
  LoadingSpinner,
  Modal,
  ModalFooter,
  Input,
  Select,
  BackButton,
} from '@/components/common';
import { AddressFields } from '@/components/forms/AddressFields';
import { BrokerAdminAccount } from './BrokerAdminAccount';
import { licenseExpiryLabel, licenseExpiryState } from './licenseExpiryState';
import { useBroker, useBrokerEmployees, useBrokerUsers, useMapUserToBroker } from '@/hooks/useBrokers';
import { useUsers } from '@/hooks/useUsers';
import { useAchievement, useBulkUpsertTargets, useTargets } from '@/hooks/useTargets';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useAuthStore } from '@/store';
import { cn, formatDate } from '@/utils';
import { formatCurrency } from '@/utils/format';
import api, { getErrorMessage } from '@/services/api';
import toast from 'react-hot-toast';
import type { Broker, BrokerUser } from '@/types';

interface AssignedInsurer {
  id: string;
  name: string;
  code: string;
  relation_status: string;
}

const countryAdminRoleOptions = [
  { value: 'ADMIN', label: 'Broker Admin' },
];

const brokerAdminRoleOptions = [
  { value: 'MANAGER', label: 'Manager' },
  { value: 'AGENT', label: 'Agent' },
  { value: 'VIEWER', label: 'Viewer' },
];

const STATUS_LABELS: Record<string, string> = {
  active: 'Live',
  pending: 'Pending Approval',
  suspended: 'Suspended',
  blocked: 'Blocked',
};

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  pending: 'warning',
  suspended: 'default',
  blocked: 'error',
};

const statusLabel = (s?: string) => (s ? STATUS_LABELS[s] || s : 'N/A');
const statusVariant = (s?: string) => (s ? STATUS_VARIANTS[s] || 'default' : 'default');

const LICENSE_TYPE_OPTIONS = [
  { value: 'direct', label: 'Direct Broker' },
  { value: 'composite', label: 'Composite Broker' },
  { value: 'reinsurance', label: 'Reinsurance Broker' },
];

/**
 * The broker record as this page actually reads it.
 *
 * `Broker` in `@/types` is narrower than what `BrokerSerializer` sends —
 * licence type/start, the tax identifiers and the admin-user handles all come
 * back on the payload but are not declared there, and `@/types` belongs to no
 * single screen. Same local widening the quote and lead pages use, for the
 * same reason.
 */
interface BrokerRecord {
  license_type?: string | null;
  license_valid_from?: string | null;
  pan_number?: string | null;
  gst_number?: string | null;
  cin_number?: string | null;
  website?: string | null;
  admin_id?: string | null;
  admin_name?: string | null;
  admin_email?: string | null;
  admin_phone?: string | null;
  custom_domain?: string;
  primary_color?: string;
}

/**
 * Every field the merged Broker + Contact form owns, in payload order.
 *
 * `legal_name` and `code` are deliberately NOT here.
 *
 * The owner's decision is that "Legal Name" and "Broker Username" come off the
 * COUNTRY ADMIN's form — not off the model. `Broker.code` in particular is the
 * first segment of every policy number this broker issues
 * (`PolicyNumberGenerator.generate_policy_number`), so the column stays, the
 * serializer still carries it, and the broker's own record still has a legal
 * name. What changes is that this screen neither shows them nor SENDS them: a
 * hidden field that still rides along in the PATCH is not hidden, it is just
 * invisible — and it can still clobber the stored value with whatever this
 * form last seeded.
 */
const EDITABLE_FIELDS = [
  'name',
  'website',
  'license_number',
  'license_type',
  'license_valid_from',
  'license_valid_until',
  'pan_number',
  'gst_number',
  'cin_number',
  'email',
  'phone',
  'address_line1',
  'address_line2',
  'city',
  'state',
  'pincode',
] as const;

type EditableField = (typeof EDITABLE_FIELDS)[number];
type BrokerForm = Record<EditableField, string>;

const EMPTY_FORM = Object.fromEntries(EDITABLE_FIELDS.map((f) => [f, ''])) as BrokerForm;

/** Dates must go as null, not "", or DRF rejects them. */
const DATE_FIELDS: EditableField[] = ['license_valid_from', 'license_valid_until'];

type TabId = 'overview' | 'admin' | 'performance';

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: BuildingOffice2Icon },
  { id: 'admin', label: 'Broker Admin', icon: ShieldCheckIcon },
  { id: 'performance', label: 'Performance & Targets', icon: ChartBarIcon },
];

const isTabId = (value: string | null): value is TabId =>
  TABS.some((tab) => tab.id === value);

/** The read-only rendering of the form above, field by field.
 *
 * Derived from one list rather than a second copy of the JSX so view and edit
 * cannot drift apart — which is the exact complaint this lane exists to fix.
 */
const VIEW_FIELDS: { label: string; field: EditableField; format?: (v: string) => string }[] = [
  { label: 'Broker Name', field: 'name' },
  { label: 'Domain Name', field: 'website' },
  { label: 'Licence Number', field: 'license_number' },
  {
    label: 'Licence Type',
    field: 'license_type',
    format: (v) => LICENSE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v,
  },
  { label: 'Licence Valid From', field: 'license_valid_from', format: (v) => (v ? formatDate(v) : '') },
  { label: 'Licence Expiry', field: 'license_valid_until', format: (v) => (v ? formatDate(v) : '') },
  { label: 'PAN', field: 'pan_number' },
  { label: 'GST number', field: 'gst_number' },
  { label: 'CIN', field: 'cin_number' },
  { label: 'Email', field: 'email' },
  { label: 'Mobile', field: 'phone' },
  { label: 'Address line 1', field: 'address_line1' },
  { label: 'Address line 2', field: 'address_line2' },
  { label: 'City', field: 'city' },
  { label: 'State', field: 'state' },
  { label: 'Pincode', field: 'pincode' },
];

export function BrokerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const [showMapUserModal, setShowMapUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // The tab is URL state, not component state. Two things depend on that: a
  // link can land on Broker Admin (which is how Edit Admin's Back gets home
  // instead of dropping the person on Overview), and a reload keeps the tab.
  // An unrecognised `?tab=` falls back to Overview rather than rendering an
  // empty page.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = isTabId(tabParam) ? tabParam : 'overview';
  const setActiveTab = useCallback(
    (tab: TabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === 'overview') next.delete('tab');
          else next.set('tab', tab);
          // Leaving the form's edit mode behind is deliberate: switching tab is
          // not "keep editing over there".
          next.delete('edit');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  // `?edit=1` opens the broker straight in edit mode. The Edit pencil on All
  // Brokers uses it, so there is ONE broker form rather than a modal that
  // edits a different subset of the same columns.
  const openInEditMode = searchParams.get('edit') === '1';

  const [assignedInsurers, setAssignedInsurers] = useState<AssignedInsurer[] | null>(null);
  const [summary, setSummary] = useState<{
    channels_count?: number;
    policies_access?: number;
    api_access_count?: number;
  } | null>(null);

  const { data: broker, isLoading, error, refetch: fetchBroker } = useBroker(id!);
  const { data: employees } = useBrokerEmployees(id!);
  const { data: brokerUsers } = useBrokerUsers(id!);
  const mapUser = useMapUserToBroker();

  const isCountryAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'country_admin';
  const isCountryAdminOnly = currentUser?.role === 'country_admin';
  // Country Admin manages broker-level info only; broker operational data
  // (employees, POSPs, mapped users, policy access) belongs to the broker's
  // own login per Task #178.
  const showBrokerOps = !isCountryAdminOnly;
  // Assigning a Broker Admin is exactly what `map_user` lets a country admin do
  // (and ONLY the ADMIN role — the server enforces that). The old gate here was
  // `super_admin || broker_admin`, which excluded the country admin whose job
  // this is, on a page only country/super admins can open at all.
  const canMapUser = isCountryAdmin;
  const canChangeStatus = isCountryAdmin;
  const canEdit = isCountryAdmin;
  const roleOptions = currentUser?.role === 'broker_admin' ? brokerAdminRoleOptions : countryAdminRoleOptions;

  useEffect(() => {
    if (!id || !isCountryAdmin) return;
    // `null` is "not answered yet / failed"; an empty array is "there are
    // none". Collapsing the two would have a failed request render as an
    // authoritative zero next to the Insurers button.
    api.get(`/brokers/${id}/assigned_insurers/`)
      .then((res) => {
        setAssignedInsurers(res.data.filter((i: AssignedInsurer) => i.relation_status === 'ACTIVE'));
      })
      .catch((err) => {
        setAssignedInsurers(null);
        toast.error(getErrorMessage(err));
      });
    api.get(`/brokers/${id}/summary/`)
      .then((res) => setSummary(res.data))
      .catch(() => setSummary(null));
  }, [id, isCountryAdmin]);

  if (isLoading) {
    return <ContentLoader />;
  }

  if (error || !broker) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Broker not found</h2>
        <p className="text-gray-500 mt-2 dark:text-gray-400">The broker you are looking for does not exist.</p>
        <Link to="/brokers" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            Back to Brokers
          </Button>
        </Link>
      </div>
    );
  }

  const record = broker as unknown as BrokerRecord;

  const handleStatusChange = async (newStatus: string, reason: string) => {
    try {
      // `status` is read-only on the broker PATCH (it was the route by which
      // a suspended broker's admin could reactivate themselves), so approval
      // goes through the country-admin-gated, audited action.
      await api.post(`/brokers/${id}/set-status/`, { status: newStatus, reason });
      // Read back rather than trust the 200: the badge must show what the
      // server stored, not what we asked for.
      const fresh = await fetchBroker();
      if (fresh.data?.status === newStatus) {
        toast.success(`Broker ${statusLabel(newStatus).toLowerCase()}`);
      } else {
        toast.error('The status change did not stick — reload and check.');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleMapUser = (userId: string, role: string, isPrimary: boolean) => {
    mapUser.mutate(
      { brokerId: id!, userId, role, isPrimary },
      {
        onSuccess: () => {
          setShowMapUserModal(false);
          // The admin_* fields on the broker payload are derived from the
          // ADMIN membership, so the header is stale until this refetches.
          void fetchBroker();
        },
      }
    );
  };

  const insurerCount = assignedInsurers?.length ?? null;

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/brokers" />
      </div>
      <PageHeader
        title={broker.name}
        subtitle={broker.code}
        breadcrumbs={[
          { label: 'Brokers', href: '/brokers' },
          { label: broker.name },
        ]}
        actions={
          <div className="flex gap-2">
            {isCountryAdmin && broker.status === 'pending' && (
              <Button
                onClick={() => handleStatusChange('active', 'Approved')}
                leftIcon={<CheckBadgeIcon className="w-5 h-5" />}
              >
                Approve Broker
              </Button>
            )}
            {isCountryAdmin && (
              <Link to={`/brokers/${id}/insurers`}>
                <Button
                  variant="outline"
                  leftIcon={<BuildingOffice2Icon className="w-5 h-5" />}
                >
                  Insurers{insurerCount !== null && insurerCount > 0 ? ` (${insurerCount})` : ''}
                </Button>
              </Link>
            )}
            {isCountryAdmin && (
              <Link to={`/brokers/${id}/settings`}>
                <Button
                  variant="outline"
                  leftIcon={<Cog6ToothIcon className="w-5 h-5" />}
                >
                  Settings
                </Button>
              </Link>
            )}
            {showBrokerOps && (
              <Link to="/posps">
                <Button variant="outline">
                  View POSPs
                </Button>
              </Link>
            )}
          </div>
        }
      />

      <div className="border-b border-gray-200 mb-6 dark:border-gray-700">
        <nav className="flex space-x-8" aria-label="Broker sections">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary stats.
                The Insurers and Channels tiles are gone: Insurers already has a
                navigation button in the header (which is where you act on it),
                and the Channels tile linked nowhere and merely restated a count
                the broker's own portal owns. What is left is broker-operational
                and is not shown to a country admin at all, so for them the grid
                renders nothing rather than an empty frame. */}
            {showBrokerOps && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <StatCard
                  label="POSPs"
                  value={broker.posp_count ?? 0}
                  icon={<UserGroupIcon className="w-5 h-5 text-green-600 dark:text-green-400" />}
                  to={`/posps?broker=${id}`}
                />
                <StatCard
                  label="Employees"
                  value={broker.employee_count ?? 0}
                  icon={<UserGroupIcon className="w-5 h-5 text-purple-600" />}
                />
                <StatCard
                  label="Mapped Users"
                  value={brokerUsers?.length ?? 0}
                  icon={<UserPlusIcon className="w-5 h-5 text-orange-600" />}
                />
                <StatCard
                  label="Policy Access"
                  value={summary?.policies_access ?? null}
                  icon={<DocumentTextIcon className="w-5 h-5 text-pink-600" />}
                />
                <StatCard
                  label="API Access"
                  value={summary?.api_access_count ?? null}
                  icon={<KeyIcon className="w-5 h-5 text-yellow-600" />}
                />
              </div>
            )}

            <BrokerInformationForm
              brokerId={id!}
              broker={broker}
              record={record}
              canEdit={canEdit}
              canChangeStatus={canChangeStatus}
              defaultEditing={openInEditMode}
              onSaved={fetchBroker}
              onStatusChange={handleStatusChange}
            />

            {/* Employees */}
            {showBrokerOps && (
            <Card>
              <CardHeader
                title="Employees"
                subtitle={`${employees?.length || 0} team members`}
              />
              {employees && employees.length > 0 ? (
                <div className="space-y-3">
                  {employees.map((emp) => (
                    <div
                      key={emp.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-gray-700/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <UserGroupIcon className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{emp.user_name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{emp.role}</p>
                        </div>
                      </div>
                      <Badge variant={emp.is_active ? 'success' : 'default'} size="sm">
                        {emp.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No employees found.</p>
              )}
            </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Onboarding Checklist (country admin only) */}
            {isCountryAdmin && (
              <Card>
                <CardHeader
                  title="Onboarding Checklist"
                  subtitle="Details filled by broker"
                />
                <ul className="space-y-2">
                  {[
                    { label: 'Licence Number', filled: Boolean(broker.license_number) },
                    { label: 'Custom Domain', filled: Boolean(record.custom_domain) },
                    { label: 'Logo', filled: Boolean(broker.logo_url) },
                    { label: 'Brand Color', filled: Boolean(record.primary_color && record.primary_color !== '#1E40AF') },
                    { label: 'Address', filled: Boolean(broker.city && broker.pincode) },
                  ].map((item) => (
                    <li key={item.label} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">{item.label}</span>
                      {item.filled ? (
                        <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <XCircleIcon className="w-5 h-5 text-gray-300 dark:text-gray-600" />
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <StatusCard
              status={broker.status}
              canChangeStatus={canChangeStatus}
              onBlock={() => handleStatusChange('blocked', 'Blocked before deletion')}
              onDelete={() => setShowDeleteModal(true)}
            />

            {/* The month-wise target grid already worked; it was simply not
                findable — it lives behind a tab called "Performance & Targets"
                and nothing on Overview or the broker list pointed at it. This
                is a signpost, NOT a second targets editor. */}
            {isCountryAdmin && (
              <Card>
                <CardHeader title="Targets" subtitle="This broker's monthly premium target" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  A broker&apos;s month-by-month target is yours to set. Twelve cells, one year at a
                  time.
                </p>
                <Button
                  variant="outline"
                  className="mt-3 w-full justify-start"
                  leftIcon={<ChartBarIcon className="w-4 h-4" />}
                  onClick={() => setActiveTab('performance')}
                >
                  Set monthly targets
                </Button>
              </Card>
            )}

            {/* Licence Information */}
            <Card>
              <CardHeader title="Licence Information" />
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <IdentificationIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Licence Number</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{broker.license_number || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <CalendarIcon className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Licence Expiry</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {broker.license_valid_until ? formatDate(broker.license_valid_until) : '-'}
                    </p>
                    {licenseExpiryLabel(broker.license_valid_until) && (
                      <Badge
                        variant={licenseExpiryState(broker.license_valid_until) === 'expired' ? 'error' : 'warning'}
                        size="sm"
                        className="mt-1"
                      >
                        {licenseExpiryLabel(broker.license_valid_until)}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Quick Actions */}
            {currentUser?.role !== 'country_admin' && (
              <Card>
                <CardHeader title="Quick Actions" />
                <div className="space-y-3">
                  <Link to="/posps" className="block">
                    <Button variant="outline" className="w-full justify-start">
                      Manage POSPs
                    </Button>
                  </Link>
                  <Link to={`/policies?broker=${id}`} className="block">
                    <Button variant="outline" className="w-full justify-start">
                      View Policies
                    </Button>
                  </Link>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {activeTab === 'admin' && (
        <BrokerAdminPanel
          brokerId={id!}
          record={record}
          brokerUsers={brokerUsers}
          canMapUser={canMapUser}
          canManageAdmin={isCountryAdminOnly}
          onAssign={() => setShowMapUserModal(true)}
        />
      )}

      {activeTab === 'performance' && (
        <BrokerPerformancePanel brokerId={id!} canSetTargets={isCountryAdmin} />
      )}

      {/* Map User Modal */}
      <MapUserModal
        isOpen={showMapUserModal}
        onClose={() => setShowMapUserModal(false)}
        onSubmit={handleMapUser}
        isLoading={mapUser.isPending}
        roleOptions={roleOptions}
      />

      <DeleteBrokerModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        brokerId={id!}
        brokerName={broker.name}
        onDeleted={() => {
          setShowDeleteModal(false);
          navigate('/brokers');
        }}
      />
    </div>
  );
}

// ===========================================================================
// Broker Information — one editable form, not two read-only cards
// ===========================================================================

/**
 * Broker Information and Contact Information used to be two read-only cards
 * sitting above a Settings page that edited half the same columns from a
 * separate "Broker" tab. Three surfaces, one record, and the only editable one
 * was the one you had to leave the page to find.
 *
 * This is that record, once, editable in place. The Active/Inactive switch is
 * NOT part of the PATCH: `status` is read-only on the broker serializer on
 * purpose (a suspended broker's own admin could otherwise patch themselves
 * back to active), so it goes through the audited `set-status` action. Inactive
 * means *suspended* — the state that bars the broker's whole team from signing
 * in — and the label says so rather than inventing a fourth meaning.
 */
function BrokerInformationForm({
  brokerId,
  broker,
  record,
  canEdit,
  canChangeStatus,
  defaultEditing = false,
  onSaved,
  onStatusChange,
}: {
  brokerId: string;
  /** The broker record; read through `BrokerRecord`/`seed` for the fields
   *  `@/types` does not declare. */
  broker: Broker;
  record: BrokerRecord;
  canEdit: boolean;
  canChangeStatus: boolean;
  /** `?edit=1` — the Edit pencil on All Brokers lands here already editing. */
  defaultEditing?: boolean;
  onSaved: () => Promise<unknown>;
  onStatusChange: (status: string, reason: string) => Promise<void>;
}) {
  const [form, setForm] = useState<BrokerForm>(EMPTY_FORM);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Opening a broker from the list is a READ. Editing is a separate, deliberate
  // act — which is what the Edit button beside the Active switch is for.
  const [isEditing, setIsEditing] = useState(canEdit && defaultEditing);
  // This form is the ONLY editor of a broker record now that the All Brokers
  // pencil lands here, so it owns the cache the retired modal's
  // `useUpdateBroker` used to own. Without this, `useBrokers`
  // (queryKey ['brokers', page, filters], staleTime 5min,
  // refetchOnWindowFocus false) keeps serving the pre-save row and the list
  // still shows the OLD name after a "saved" toast.
  const queryClient = useQueryClient();

  const seed = useCallback(() => {
    const source = broker as unknown as Record<string, unknown>;
    const next = { ...EMPTY_FORM };
    EDITABLE_FIELDS.forEach((field) => {
      const value = source[field] ?? (record as unknown as Record<string, unknown>)[field];
      next[field] = value == null ? '' : String(value);
    });
    if (!next.license_type) next.license_type = 'direct';
    setForm(next);
    setIsDirty(false);
    setFieldErrors({});
  }, [broker, record]);

  useEffect(() => {
    seed();
  }, [seed]);

  const update = (field: EditableField, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setFieldErrors({});
    const payload: Record<string, unknown> = {};
    EDITABLE_FIELDS.forEach((field) => {
      payload[field] = DATE_FIELDS.includes(field) && !form[field] ? null : form[field];
    });

    try {
      await api.patch(`/brokers/${brokerId}/`, payload);
      // Only after the PATCH RESOLVES: a failed save must leave the cached list
      // alone rather than refetch it into looking like something happened.
      queryClient.invalidateQueries({ queryKey: ['brokers'] });
      queryClient.invalidateQueries({ queryKey: ['broker', brokerId] });
      // The signature bug on this platform is a save that reports success and
      // stores nothing, so the toast waits for the RE-READ. `onSaved` refetches
      // the broker; the effect above then re-seeds this form from whatever the
      // server actually holds, so what is on screen after a save is the stored
      // record and not the typed one.
      const fresh = (await onSaved()) as { data?: Record<string, unknown> } | undefined;
      const stored = fresh?.data;
      const drifted = stored
        ? EDITABLE_FIELDS.filter((field) => {
            const sent = form[field].trim();
            if (!sent) return false;
            const got = stored[field];
            return got != null && String(got).trim() !== sent;
          })
        : [];
      setIsEditing(false);
      if (drifted.length > 0) {
        // Not a failure — the server normalises phone numbers and may reformat
        // a date — but the person must see that what was stored differs from
        // what they typed rather than discovering it later.
        toast.success(`Saved. Stored differently: ${drifted.join(', ')}.`);
      } else {
        toast.success('Broker details saved');
      }
    } catch (err) {
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const errors: Record<string, string> = {};
        Object.entries(data).forEach(([key, value]) => {
          errors[key] = Array.isArray(value) ? String(value[0]) : String(value);
        });
        setFieldErrors(errors);
      }
      toast.error(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const isActive = broker.status === 'active';

  return (
    <Card>
      <CardHeader
        title="Broker Information"
        subtitle="Identity, licensing and contact details — the whole record"
      />

      {(canChangeStatus || canEdit) && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-700/50">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {isActive ? 'Active' : 'Inactive'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isActive
                ? 'Live. The broker and their whole team can sign in.'
                : `Currently ${statusLabel(broker.status).toLowerCase()}. Nobody at this broker can sign in.`}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {canChangeStatus && (
              <label className="flex cursor-pointer items-center gap-2">
                <span className="sr-only">Broker active</span>
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Broker active"
                  checked={isActive}
                  disabled={broker.status === 'blocked'}
                  onChange={(e) =>
                    onStatusChange(
                      e.target.checked ? 'active' : 'suspended',
                      e.target.checked ? 'Reactivated from broker page' : 'Suspended from broker page'
                    )
                  }
                  className="relative h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-gray-400 transition-colors before:absolute before:left-0.5 before:top-0.5 before:h-4 before:w-4 before:rounded-full before:bg-white before:shadow before:transition-transform checked:bg-green-600 checked:before:translate-x-4 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-500 dark:checked:bg-green-500"
                />
                <span
                  data-testid="broker-active-state"
                  className={`min-w-[2.25rem] text-sm font-semibold ${
                    isActive ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {isActive ? 'ON' : 'OFF'}
                </span>
              </label>
            )}
            {canEdit && !isEditing && (
              <Button
                variant="outline"
                size="sm"
                type="button"
                leftIcon={<PencilSquareIcon className="w-4 h-4" />}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </div>
        </div>
      )}

      {!isEditing ? (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          {VIEW_FIELDS.map(({ label, field, format }) => {
            const raw = form[field];
            return (
              <div key={field}>
                <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {label}
                </dt>
                <dd className="mt-0.5 text-sm text-gray-900 break-words dark:text-white">
                  {raw ? (format ? format(raw) : raw) : '—'}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Broker Name"
            name="name"
            value={form.name}
            disabled={!canEdit}
            error={fieldErrors.name}
            onChange={(e) => update('name', e.target.value)}
          />
          {/* "Domain Name", not "Website" — the owner's word, and the same
              label the Add Broker form uses, on the same column. It is NOT
              `custom_domain`: that is the white-label ROUTING host, it has
              exactly one writer (`BrokerSettingsSerializer`, from the broker's
              own Settings > Domain screen), and a second uncoordinated writer
              to a routing-relevant column is not worth a label. */}
          <Input
            label="Domain Name"
            name="website"
            value={form.website}
            disabled={!canEdit}
            error={fieldErrors.website}
            onChange={(e) => update('website', e.target.value)}
            placeholder="https://example.com"
            helperText="The broker's own web address. The white-label portal domain is set on Settings › Domain."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Licence Number"
            name="license_number"
            value={form.license_number}
            disabled={!canEdit}
            error={fieldErrors.license_number}
            onChange={(e) => update('license_number', e.target.value)}
          />
          <Select
            label="Licence Type"
            name="license_type"
            value={form.license_type || 'direct'}
            onChange={(e) => update('license_type', e.target.value)}
            options={LICENSE_TYPE_OPTIONS}
          />
          <Input
            label="Licence Valid From"
            name="license_valid_from"
            type="date"
            value={form.license_valid_from}
            disabled={!canEdit}
            max={form.license_valid_until || undefined}
            error={fieldErrors.license_valid_from}
            onChange={(e) => update('license_valid_from', e.target.value)}
          />
          <Input
            label="Licence Expiry"
            name="license_valid_until"
            type="date"
            value={form.license_valid_until}
            disabled={!canEdit}
            min={form.license_valid_from || undefined}
            error={fieldErrors.license_valid_until}
            onChange={(e) => update('license_valid_until', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input
            label="PAN"
            name="pan_number"
            value={form.pan_number}
            disabled={!canEdit}
            error={fieldErrors.pan_number}
            onChange={(e) => update('pan_number', e.target.value.toUpperCase())}
          />
          <Input
            label="GST number"
            name="gst_number"
            value={form.gst_number}
            disabled={!canEdit}
            error={fieldErrors.gst_number}
            onChange={(e) => update('gst_number', e.target.value.toUpperCase())}
          />
          <Input
            label="CIN"
            name="cin_number"
            value={form.cin_number}
            disabled={!canEdit}
            error={fieldErrors.cin_number}
            onChange={(e) => update('cin_number', e.target.value.toUpperCase())}
          />
        </div>

        <div className="border-t border-gray-100 pt-4 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Contact</h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Email and mobile are shared with the broker admin's login — changing them here changes
            what they sign in with.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Email"
            name="email"
            type="email"
            value={form.email}
            disabled={!canEdit}
            error={fieldErrors.email}
            onChange={(e) => update('email', e.target.value)}
          />
          <Input
            label="Mobile"
            name="phone"
            value={form.phone}
            disabled={!canEdit}
            error={fieldErrors.phone}
            onChange={(e) => update('phone', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Address line 1"
            name="address_line1"
            value={form.address_line1}
            disabled={!canEdit}
            error={fieldErrors.address_line1}
            onChange={(e) => update('address_line1', e.target.value)}
          />
          <Input
            label="Address line 2"
            name="address_line2"
            value={form.address_line2}
            disabled={!canEdit}
            error={fieldErrors.address_line2}
            onChange={(e) => update('address_line2', e.target.value)}
          />
          {/* The SAME address control Add Broker uses (B6/B9: a PIN pre-fills
              State and City, and State is always a dropdown). It used to live
              only on the list's quick-edit modal; that modal is gone, and the
              behaviour belongs on the one broker form rather than with it. */}
          <AddressFields
            pincode={form.pincode}
            onPincodeChange={(value) => update('pincode', value)}
            pincodeError={fieldErrors.pincode}
            city={form.city}
            onCityChange={(value) => update('city', value)}
            cityError={fieldErrors.city}
            state={form.state}
            onStateChange={(value) => update('state', value)}
            stateError={fieldErrors.state}
            disabled={!canEdit}
          />
        </div>

        {canEdit && (
          <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
            {/* Cancel, not Discard: it leaves edit mode whether or not
                anything was typed. Disabling it on a clean form would have
                trapped somebody who pressed Edit by accident. */}
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                seed();
                setIsEditing(false);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} isLoading={isSaving} disabled={!isDirty}>
              Save broker details
            </Button>
          </div>
        )}
      </div>
      )}
    </Card>
  );
}

// ===========================================================================
// Status, blocking and the guarded delete
// ===========================================================================

/**
 * What is left of the old three-button status row.
 *
 * Active/Inactive moved into the form above (one switch, one meaning), so what
 * remains here is the escalation ladder that has no undo:
 *
 *   Active → (switch off) Suspended → Block → Delete
 *
 * Block is deliberately only offered from *suspended*. Removing it outright
 * would have stranded the state — `blocked` is a real status on the model, it
 * bars every login, and it is now the precondition the API enforces for a
 * delete — so instead of a peer of Suspend it became the step before deletion.
 * Brokers already blocked are not stranded either: they still show Delete, and
 * the switch above turns them back on.
 */
function StatusCard({
  status,
  canChangeStatus,
  onBlock,
  onDelete,
}: {
  status: string;
  canChangeStatus: boolean;
  onBlock: () => void;
  onDelete: () => void;
}) {
  return (
    <Card>
      <CardHeader title="Status" />
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">Account Status</span>
          <Badge variant={statusVariant(status)}>{statusLabel(status)}</Badge>
        </div>

        {canChangeStatus && (
          <div className="space-y-3">
            {status === 'active' && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Use the Active switch on the broker form to suspend this broker.
              </p>
            )}
            {status === 'suspended' && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Blocking is permanent lockout, and is required before a broker can be deleted.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  leftIcon={<NoSymbolIcon className="w-4 h-4" />}
                  onClick={onBlock}
                >
                  Block Broker
                </Button>
              </div>
            )}
            {status === 'blocked' && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Blocked. Deleting removes the organisation, and is refused while any business
                  still points at it.
                </p>
                <Button
                  variant="danger"
                  size="sm"
                  className="w-full justify-start"
                  leftIcon={<TrashIcon className="w-4 h-4" />}
                  onClick={onDelete}
                >
                  Delete Broker
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * The delete confirmation. Typing the broker's own username is the deliberate
 * friction: a broker cascades into policies, POSPs, contacts and commissions,
 * so this must not be reachable by muscle memory.
 *
 * The 409 the server answers with when something still points at the record is
 * rendered VERBATIM — it names what is in the way ("312 policies and 4 POSPs"),
 * which is the only thing that tells the admin what to do next.
 */
function DeleteBrokerModal({
  isOpen,
  onClose,
  brokerId,
  brokerName,
  onDeleted,
}: {
  isOpen: boolean;
  onClose: () => void;
  brokerId: string;
  brokerName: string;
  onDeleted: () => void;
}) {
  const [confirmation, setConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setConfirmation('');
      setRefusal(null);
    }
  }, [isOpen]);

  const handleDelete = async () => {
    setIsDeleting(true);
    setRefusal(null);
    try {
      await api.delete(`/brokers/${brokerId}/`);
      toast.success(`${brokerName} deleted`);
      onDeleted();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setRefusal(detail || getErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Delete ${brokerName}?`} size="md">
      <div className="space-y-4">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          This removes the broker organisation. It is refused while any policy, POSP, contact,
          quote or commission still belongs to it, and it can be undone only by an operator.
        </p>
        <Input
          label={`Type ${brokerName} to confirm`}
          // `name` is what gives the shared Input an id to bind its label to.
          name="delete-broker-confirmation"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          placeholder={brokerName}
        />
        {refusal && (
          <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:text-red-400 dark:bg-red-900/20 dark:border-red-900">
            {refusal}
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="outline" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          type="button"
          onClick={handleDelete}
          isLoading={isDeleting}
          disabled={confirmation.trim() !== brokerName}
        >
          Delete broker
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ===========================================================================
// Broker Admin tab
// ===========================================================================

/**
 * Assign a Broker Admin, edit them, and see what their rights actually are.
 *
 * The rights half is deliberately NOT a second rights editor. B4's resolver
 * treats an admin LOGIN as unrestricted before any stored row is read, and
 * `POST /employee-rights/bulk-set/` refuses to write rows for such a subject in
 * so many words: *"This employee signs in as a broker admin, which holds every
 * right on every module. Their per-employee rights are never consulted, so they
 * cannot be set here."* A rights matrix for a broker admin would therefore be a
 * grid of toggles that governs nothing — this platform's signature bug in UI
 * form. So this states the rule and points at the screen where per-person
 * rights DO apply: the broker's own Rights Management page, which edits
 * employees and POSPs.
 */
function BrokerAdminPanel({
  brokerId,
  record,
  brokerUsers,
  canMapUser,
  canManageAdmin,
  onAssign,
}: {
  brokerId: string;
  record: BrokerRecord;
  brokerUsers: BrokerUser[] | undefined;
  canMapUser: boolean;
  /**
   * Country admin only: broker admins are inside `GET /users/{id}/`'s scope
   * for a country admin and nobody else who can open this page (a super
   * admin's user scope is country admins), so the edit and account actions
   * would 404 for anyone else.
   */
  canManageAdmin: boolean;
  onAssign: () => void;
}) {
  const { data: employees, isLoading, isError, error, refetch } = useBrokerEmployees(brokerId);
  const admins = (brokerUsers ?? []).filter((bu) => bu.role === 'ADMIN' && bu.is_active);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Broker Admin"
          subtitle="The login that runs this broker"
          /* "Assign another admin" is gone by decision: a broker has ONE admin
             login, and the button invited a second one whose only visible
             trace was the "Other admin logins" list below. Assigning is still
             offered for a broker that has none — that is the case where the
             broker cannot be run at all. */
          action={
            canMapUser && !record.admin_id ? (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<UserPlusIcon className="w-4 h-4" />}
                onClick={onAssign}
              >
                Assign Broker Admin
              </Button>
            ) : undefined
          }
        />

        {record.admin_id && canManageAdmin ? (
          /* Edit, Suspend / Block / Reactivate and Reset Password happen HERE.
             They used to link out to /users/..., which a country admin is not
             routed for (B-05, M-06, M-07). */
          <BrokerAdminAccount adminId={record.admin_id} brokerId={brokerId} />
        ) : record.admin_id ? (
          <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{record.admin_name || 'Unnamed'}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{record.admin_email || 'No email'}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{record.admin_phone || 'No mobile'}</p>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center dark:border-gray-600">
            <p className="text-sm font-medium text-gray-900 dark:text-white">No Broker Admin assigned</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Nobody can sign in and run this broker until one is.
            </p>
          </div>
        )}

        {admins.length > 1 && (
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Other admin logins
            </p>
            <ul className="mt-2 space-y-2">
              {admins.map((bu) => (
                <li key={bu.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-900 dark:text-white">
                    {bu.user_name}
                    {bu.is_primary && (
                      <span className="ml-2 text-xs font-semibold text-primary-600">(Primary)</span>
                    )}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{bu.user_email || bu.user_phone}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title="Rights" subtitle="What this admin may do, and who rights apply to" />
        <div className="space-y-4 text-sm text-gray-700 dark:text-gray-300">
          <div className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-4 dark:bg-blue-900/20 dark:border-blue-900">
            <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
            <div>
              <p className="font-medium text-blue-900">
                A Broker Admin holds every right on every module.
              </p>
              <p className="mt-1 text-blue-800 dark:text-blue-300">
                That comes from the login role itself, before any stored rights row is read — so
                there is nothing to grant or deny here, and the API refuses to store per-person
                rights for an admin login. To restrict someone, give them an employee login instead
                of an admin one.
              </p>
            </div>
          </div>

          <p>
            Per-person rights apply to the broker&apos;s <strong>employees and POSPs</strong>, and
            are set by the broker admin on their own Rights Management screen.
          </p>

          {isLoading ? (
            <div className="flex justify-center py-6">
              <LoadingSpinner />
            </div>
          ) : isError ? (
            <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:text-red-400 dark:bg-red-900/20 dark:border-red-900">
              <p className="font-semibold">The staff list could not be loaded.</p>
              <p className="mt-1">{getErrorMessage(error)}</p>
              <button type="button" onClick={() => refetch()} className="mt-2 underline">
                Try again
              </button>
            </div>
          ) : !employees || employees.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">
              This broker has no employee logins yet, so there are no per-person rights to manage.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:border-gray-700 dark:divide-gray-700">
              {employees.map((emp) => (
                <li key={emp.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{emp.user_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{emp.email || emp.phone}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{emp.role}</span>
                    <Badge variant={emp.is_active ? 'success' : 'default'} size="sm">
                      {emp.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}

// ===========================================================================
// Performance & Targets tab
// ===========================================================================

interface BrokerDashboard {
  employees: { active: number };
  posps: { total: number; active: number; pending: number; training: number };
  applications: { total: number; pending: number; this_month: number };
  performance: {
    total_policies: number;
    total_premium: string;
    this_month_policies: number;
    this_month_premium: string;
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * This broker's own performance and its assigned targets, on its own page.
 *
 * Both halves already existed, one per screen and neither of them per-broker:
 * `/country/analytics` ranks every broker in the country (and its sidebar entry
 * is gone, so it is no longer reachable that way), and `/broker/targets` is the
 * broker admin's own POSP grid. Nothing reused there is rewritten here — the
 * numbers come from `/brokers/{id}/dashboard/` and `/targets/achievement/`
 * (which takes `?broker=`).
 *
 * U-03 (24 Sep): compacted to one row of KPI tiles and ONE table -- the twelve
 * month inputs as a single row, with the broker's achieved premium and % from
 * the achievement series directly beneath each month -- in place of four
 * StatCards, a 2/3/6 grid of month inputs and a separate
 * TargetVsAchievementWidget (which, for one broker, was a one-row table
 * repeating the year totals).
 *
 * The target grid is genuinely new, but only because there was nowhere to set
 * a broker's number: the API has always said the country admin owns the
 * broker-level target and the broker admin owns their POSPs', and only the
 * second half had a screen.
 */
function BrokerPerformancePanel({
  brokerId,
  canSetTargets,
}: {
  brokerId: string;
  canSetTargets: boolean;
}) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [dashboard, setDashboard] = useState<BrokerDashboard | null>(null);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [edits, setEdits] = useState<Record<number, string>>({});

  const loadDashboard = useCallback(() => {
    setDashboardLoading(true);
    setDashboardError(null);
    api
      .get<BrokerDashboard>(`/brokers/${brokerId}/dashboard/`)
      .then((res) => setDashboard(res.data))
      // A dashboard that paints zeros when its query failed is
      // indistinguishable from a broker with no business.
      .catch((err) => {
        setDashboard(null);
        setDashboardError(getErrorMessage(err));
      })
      .finally(() => setDashboardLoading(false));
  }, [brokerId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const achievement = useAchievement({
    broker: brokerId,
    from: `${year}-01`,
    to: `${year}-12`,
  });
  const targetsQuery = useTargets({ scope: 'broker', broker: brokerId, year });
  const bulkSave = useBulkUpsertTargets();

  // A different year is a different set of stored values; drop pending edits
  // rather than silently carrying them onto another year's cells.
  useEffect(() => {
    setEdits({});
  }, [year]);

  const stored = useMemo(() => {
    const map: Record<number, string> = {};
    (targetsQuery.data ?? []).forEach((t) => {
      if (!t.posp) map[t.month] = t.target_amount;
    });
    return map;
  }, [targetsQuery.data]);

  const valueFor = (month: number) => (month in edits ? edits[month] : (stored[month] ?? ''));
  const dirtyMonths = Object.keys(edits)
    .map(Number)
    .filter((month) => (edits[month] ?? '') !== (stored[month] ?? ''));

  const handleSaveTargets = () => {
    if (dirtyMonths.length === 0) return;
    bulkSave.mutate(
      dirtyMonths.map((month) => ({
        broker: brokerId,
        year,
        month,
        target_amount: edits[month] || '0',
      })),
      {
        // Clear the draft only once the server has answered, then let the
        // query refetch decide what the cells show.
        onSuccess: () => setEdits({}),
      }
    );
  };

  // U-03: the broker's own row of the achievement envelope, month by month,
  // so the target inputs and what was achieved sit in the same column.
  const brokerRow = (achievement.data?.rows ?? []).find(
    (r) => r.entity_type === 'broker' && r.entity_id === brokerId
  );
  const achievedByMonth = useMemo(() => {
    const map: Record<number, { achieved: number; percentage: number | null }> = {};
    (brokerRow?.series ?? []).forEach((point) => {
      if (point.year !== year) return;
      map[point.month] = { achieved: Number(point.achieved) || 0, percentage: point.percentage };
    });
    return map;
  }, [brokerRow, year]);
  const targetTotal = MONTHS.reduce((sum, _m, i) => sum + (Number(valueFor(i + 1)) || 0), 0);

  const perfValue = (value: number | string) =>
    dashboardLoading ? null : dashboardError || !dashboard ? '—' : value;
  const achievementUnavailable = achievement.isLoading || achievement.isError;
  const cellTh =
    'px-1.5 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
  const rowTh =
    'sticky left-0 z-[1] bg-white px-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400';

  return (
    <div className="space-y-4">
      {/* U-03: one row of KPI tiles -- the four performance figures and the
          year's target vs achievement -- in place of a StatCard card, a
          target card and a separate achievement card. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6" data-testid="broker-performance-kpis">
        <PerformanceTile
          label="Policies sold"
          value={perfValue(dashboard?.performance.total_policies ?? 0)}
        />
        <PerformanceTile
          label="Premium"
          value={perfValue(formatCurrency(dashboard?.performance.total_premium ?? '0'))}
        />
        <PerformanceTile
          label="This month"
          value={perfValue(dashboard?.performance.this_month_policies ?? 0)}
          hint="Policies"
        />
        <PerformanceTile label="Active POSPs" value={perfValue(dashboard?.posps.active ?? 0)} />
        <PerformanceTile
          label={`Target · ${year}`}
          value={targetsQuery.isLoading ? null : targetTotal > 0 ? formatCurrency(targetTotal) : '—'}
          hint={targetTotal > 0 ? undefined : 'No target set'}
        />
        <PerformanceTile
          label="Achieved"
          value={
            achievement.isLoading
              ? null
              : achievement.isError || !brokerRow
                ? '—'
                : formatCurrency(brokerRow.achieved)
          }
          // A missing target is UNKNOWN, not 0% -- the widget's rule.
          hint={
            achievement.isError
              ? 'Could not load'
              : brokerRow && brokerRow.percentage !== null
                ? `${brokerRow.percentage}% of target`
                : 'From issued policies'
          }
        />
      </div>

      {dashboardError && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:text-red-400 dark:bg-red-900/20 dark:border-red-900">
          <span className="font-semibold">Performance could not be loaded.</span> {dashboardError}{' '}
          <button type="button" onClick={loadDashboard} className="underline">
            Try again
          </button>
        </div>
      )}

      <Card padding="none" className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Targets vs achievement · {year}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {canSetTargets
                ? 'Monthly premium targets; achieved is premium on issued policies.'
                : "A broker's own monthly target is set by the country admin."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canSetTargets && dirtyMonths.length > 0 && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                {dirtyMonths.length} unsaved {dirtyMonths.length === 1 ? 'month' : 'months'}
              </span>
            )}
            <Select
              aria-label="Target year"
              options={[currentYear - 1, currentYear, currentYear + 1].map((y) => ({
                value: String(y),
                label: String(y),
              }))}
              value={String(year)}
              onChange={(e) => setYear(Number(e.target.value))}
            />
            {canSetTargets && (
              <Button
                type="button"
                size="sm"
                onClick={handleSaveTargets}
                isLoading={bulkSave.isPending}
                disabled={dirtyMonths.length === 0 || targetsQuery.isLoading || targetsQuery.isError}
              >
                Save targets
              </Button>
            )}
          </div>
        </div>

        {targetsQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : targetsQuery.isError ? (
          <div role="alert" className="m-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:text-red-400 dark:bg-red-900/20 dark:border-red-900">
            <p className="font-semibold">Targets could not be loaded.</p>
            <p className="mt-1">{getErrorMessage(targetsQuery.error)}</p>
            <button type="button" onClick={() => targetsQuery.refetch()} className="mt-2 underline">
              Try again
            </button>
          </div>
        ) : (
          <>
            {/* The twelve months as ONE compact row of inputs, with what was
                achieved and the percentage directly beneath each. Scrolls
                sideways inside the card on a narrow screen. */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[56rem] table-fixed border-separate border-spacing-0 text-sm">
                <colgroup>
                  <col className="w-24" />
                  {MONTHS.map((m) => (
                    <col key={m} />
                  ))}
                  <col className="w-28" />
                </colgroup>
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900">
                    <th scope="col" className={`${rowTh} bg-gray-50 dark:bg-gray-900`}>
                      <span className="sr-only">Measure</span>
                    </th>
                    {MONTHS.map((label, index) => (
                      <th key={label} scope="col" className={cellTh}>
                        {/* The month header IS the input's label, so the cell
                            is still found as "Mar" by a screen reader. */}
                        <label htmlFor={`target-${year}-${index + 1}`}>{label}</label>
                      </th>
                    ))}
                    <th scope="col" className={`${cellTh} pr-3`}>
                      Year
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row" className={rowTh}>
                      Target
                    </th>
                    {MONTHS.map((label, index) => {
                      const month = index + 1;
                      const dirty = dirtyMonths.includes(month);
                      return (
                        <td key={label} className="px-0.5 py-1">
                          <input
                            id={`target-${year}-${month}`}
                            name={`target-${year}-${month}`}
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={valueFor(month)}
                            disabled={!canSetTargets || bulkSave.isPending}
                            onChange={(e) =>
                              setEdits((prev) => ({ ...prev, [month]: e.target.value }))
                            }
                            placeholder="—"
                            className={cn(
                              'h-8 w-full min-w-0 rounded-md border px-1.5 text-right text-xs tabular-nums text-gray-900 placeholder-gray-400 dark:text-gray-100 dark:placeholder-gray-500',
                              '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                              'focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400',
                              'disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800',
                              dirty
                                ? 'border-amber-400 bg-amber-50 dark:border-amber-500 dark:bg-amber-900/20'
                                : 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-900'
                            )}
                          />
                        </td>
                      );
                    })}
                    <td className="px-3 py-1 text-right text-xs font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                      {targetTotal > 0 ? formatCurrency(targetTotal) : '—'}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className={rowTh}>
                      Achieved
                    </th>
                    {MONTHS.map((label, index) => {
                      const ach = achievedByMonth[index + 1];
                      return (
                        <td
                          key={label}
                          data-testid={`achieved-${index + 1}`}
                          className="truncate px-1.5 py-1 text-right text-xs tabular-nums text-gray-700 dark:text-gray-300"
                          title={ach ? formatCurrency(ach.achieved) : undefined}
                        >
                          {achievementUnavailable ? '—' : ach && ach.achieved > 0 ? compactInr.format(ach.achieved) : '—'}
                        </td>
                      );
                    })}
                    <td className="px-3 py-1 text-right text-xs font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                      {achievementUnavailable || !brokerRow ? '—' : formatCurrency(brokerRow.achieved)}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className={`${rowTh} border-t border-gray-100 dark:border-gray-700`}>
                      %
                    </th>
                    {MONTHS.map((label, index) => {
                      const ach = achievedByMonth[index + 1];
                      return (
                        <td
                          key={label}
                          className={cn(
                            'border-t border-gray-100 px-1.5 py-1 text-right text-xs font-semibold tabular-nums dark:border-gray-700',
                            achievementTone(ach?.percentage ?? null)
                          )}
                        >
                          {achievementUnavailable || !ach || ach.percentage === null ? '—' : `${ach.percentage}%`}
                        </td>
                      );
                    })}
                    <td
                      className={cn(
                        'border-t border-gray-100 px-3 py-1 text-right text-xs font-semibold tabular-nums dark:border-gray-700',
                        achievementTone(brokerRow?.percentage ?? null)
                      )}
                    >
                      {achievementUnavailable || !brokerRow || brokerRow.percentage === null
                        ? '—'
                        : `${brokerRow.percentage}%`}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {achievement.isError && (
              <div role="alert" className="border-t border-gray-100 px-4 py-2 text-sm text-red-700 dark:border-gray-700 dark:text-red-400">
                Achievement could not be loaded. {getErrorMessage(achievement.error)}{' '}
                <button type="button" onClick={() => achievement.refetch()} className="underline">
                  Try again
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

const compactInr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/** Same thresholds as TargetVsAchievementWidget's progress bar. */
function achievementTone(percentage: number | null): string {
  if (percentage === null) return 'text-gray-400 dark:text-gray-500';
  if (percentage >= 100) return 'text-green-600 dark:text-green-400';
  if (percentage >= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

/** A compact KPI tile. `null` value = still loading (a skeleton, never a 0). */
function PerformanceTile({ label, value, hint }: { label: string; value: number | string | null; hint?: string }) {
  return (
    <Card padding="sm" className="min-w-0">
      <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </p>
      {value === null ? (
        <div className="mt-1.5 h-6 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
      ) : (
        <p className="mt-1 truncate text-lg font-semibold tabular-nums text-gray-900 dark:text-white">{value}</p>
      )}
      {hint && <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
    </Card>
  );
}

// ===========================================================================

function MapUserModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  roleOptions,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userId: string, role: string, isPrimary: boolean) => void;
  isLoading: boolean;
  roleOptions: { value: string; label: string }[];
}) {
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState(roleOptions[0]?.value || 'ADMIN');
  const [isPrimary, setIsPrimary] = useState(false);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `searchTerm` drives the Input synchronously, `debouncedSearchTerm`
  // (300ms) is what actually feeds the query below.
  const { term: searchTerm, debouncedTerm: debouncedSearchTerm, setTerm: setSearchTerm } = useDebouncedSearch();

  const { data: usersData } = useUsers(1, debouncedSearchTerm ? { search: debouncedSearchTerm } : undefined);
  const users = usersData?.results ?? [];

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setUserId('');
      setRole(roleOptions[0]?.value || 'ADMIN');
      setIsPrimary(false);
      setSearchTerm('');
    }
  }, [isOpen, roleOptions, setSearchTerm]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    onSubmit(userId, role, isPrimary);
  };

  const selectedUser = users.find((u) => u.id === userId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Map User to Broker" size="md">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Search User *
            </label>
            <Input
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setUserId('');
              }}
              placeholder="Search by name, email, or phone..."
            />
            {/* Self-review follow-up: gate on `debouncedSearchTerm` (what
                `users` was actually fetched for), not `searchTerm` (updates
                synchronously on every keystroke). Gating on the un-debounced
                term let this render the PREVIOUS query's results -- kept
                around by useUsers' own `placeholderData` while the new one
                loads -- as though they matched what was just typed, and each
                stale row was clickable: typing "z" could show and let you
                pick a user that doesn't actually match "z" for the ~300ms
                debounce window. */}
            {debouncedSearchTerm && users.length > 0 && !userId && (
              <div className="mt-1 border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white shadow-sm dark:bg-gray-800 dark:border-gray-700">
                {users.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => {
                      setUserId(u.id);
                      setSearchTerm(u.full_name || u.email || u.phone || '');
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 dark:border-gray-700 dark:hover:bg-gray-700/50"
                  >
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{u.full_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {u.email || 'No email'} {u.phone ? `| ${u.phone}` : ''} | {u.role}
                    </p>
                  </button>
                ))}
              </div>
            )}
            {debouncedSearchTerm && users.length === 0 && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">No users found.</p>
            )}
            {selectedUser && (
              <div className="mt-2 p-2 bg-primary-50 rounded-lg">
                <p className="text-sm font-medium text-primary-700">{selectedUser.full_name}</p>
                <p className="text-xs text-primary-600">
                  {selectedUser.email || selectedUser.phone} | {selectedUser.role}
                </p>
              </div>
            )}
          </div>
          <Select
            label="Role"
            options={roleOptions}
            value={role}
            onChange={(e) => setRole(e.target.value)}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Set as primary broker for this user</span>
          </label>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading} disabled={!userId}>
            Map User
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function StatCard({
  label,
  value,
  icon,
  to,
}: {
  label: string;
  /** `null` means "we could not read this", and renders as a dash — never 0. */
  value: number | string | null;
  icon: React.ReactNode;
  to?: string;
}) {
  const inner = (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded-lg">{icon}</div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-semibold text-gray-900 dark:text-white">
            {value === null ? '—' : value}
          </p>
        </div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}
